import { useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { fireFirstSave } from '@/hooks/useOnboardingTelemetry';
import { sanitizeErrorMessage } from '@/lib/errors';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';
import { isMultiSheetState } from '@/types/workflow';
import { supabase } from '@/integrations/supabase/client';

/**
 * Samla entity-id:n (blocks + groups) från en single- eller multi-sheet-
 * state. Används av prune-RPC:n för att städa entity_details-rader vars
 * parent-entity har raderats i editorn.
 */
function collectActiveEntityIds(state: WorkflowState | MultiSheetWorkflowState): string[] {
  const ids: string[] = [];
  if (isMultiSheetState(state)) {
    for (const sheet of state.sheets) {
      for (const b of sheet.blocks) ids.push(b.id);
      for (const g of sheet.groups) ids.push(g.id);
    }
  } else {
    for (const b of state.blocks) ids.push(b.id);
    for (const g of state.groups) ids.push(g.id);
  }
  return ids;
}

async function supabasePruneEntityDetails(workflowId: string, activeIds: string[]): Promise<void> {
  try {
    await supabase.rpc('prune_entity_details_for_workflow', {
      p_workflow_id: workflowId,
      p_active_entity_ids: activeIds,
    });
  } catch (err) {
    console.warn('[save] prune_entity_details_for_workflow failed', err);
  }
}

// A-015: signaturerna breddade till union så Index.tsx inte längre behöver
// `as unknown as`-casts. getState() från useMultiSheetWorkflow returnerar
// MultiSheetWorkflowState; saveDocument() från useDocuments accepterar
// båda. Vi serialiserar staten som JSON så shape-skillnaden är transparent
// här — vidare normalisering ligger i loadState-konsumenten.
export type SaveableWorkflowState = WorkflowState | MultiSheetWorkflowState;

export interface UseSaveHandlersDeps {
  getState: () => SaveableWorkflowState;
  workflowName: string;
  setWorkflowName: (name: string) => void;
  currentDocumentId: string | null;
  setCurrentDocumentId: (id: string | null) => void;
  markClean: () => void;
  logAction: (action: string, detail?: string) => void;
  t: (key: string) => string;
  saveDocument: (
    title: string,
    state: SaveableWorkflowState,
    category: string,
    existingId?: string,
    isOfficial?: boolean,
  ) => Promise<{ id: string } | null>;
  setSaveAsNew: (v: boolean) => void;
  setSaveDialogOpen: (v: boolean) => void;
}

export function useSaveHandlers(deps: UseSaveHandlersDeps) {
  const {
    getState, workflowName, setWorkflowName, currentDocumentId,
    setCurrentDocumentId, markClean, logAction, t, saveDocument,
    setSaveAsNew, setSaveDialogOpen,
  } = deps;

  /** Download the current workflow as a JSON file. */
  const handleSave = useCallback(() => {
    const state = getState();
    state.name = workflowName;
    const json = JSON.stringify(state, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `${workflowName || 'workflow'}.json`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    markClean();
    logAction('Saved', `Downloaded as ${link.download}`);
    fireFirstSave({ destination: 'file' });
    toast({ title: t('saved'), description: t('savedAsFile') });
  }, [getState, workflowName, markClean, logAction, t]);

  /** Open SaveDialog in "save as new" mode. */
  const handleSaveAs = useCallback(() => {
    setSaveAsNew(true);
    setSaveDialogOpen(true);
  }, [setSaveAsNew, setSaveDialogOpen]);

  /** Persist to Supabase via useDocuments.saveDocument. */
  const handleSaveToDb = useCallback(async (
    title: string,
    category: string,
    asNew: boolean,
    isOfficial: boolean,
  ) => {
    const state = getState();
    state.name = title;
    try {
      const result = await saveDocument(
        title,
        state,
        category,
        asNew ? undefined : currentDocumentId ?? undefined,
        isOfficial,
      );
      if (result) {
        if (asNew || !currentDocumentId) setCurrentDocumentId(result.id);
        setWorkflowName(title);
        markClean();
        fireFirstSave({ destination: 'cloud' });
        toast({ title: t('saved'), description: `"${title}" ${t('savedToDb')}` });
        // Fas 5-6: städa orphan entity_details (entities som har raderats
        // i editorn men har kvar rader i DB:n). Fire-and-forget — om RPC:n
        // failar är det inte kritiskt (orphans är harmless data, inte
        // blockerande för save).
        const savedId = result.id;
        const activeIds = collectActiveEntityIds(state);
        if (savedId && activeIds.length > 0) {
          void supabasePruneEntityDetails(savedId, activeIds);
        }
      }
    } catch (err: unknown) {
      toast({ title: t('saveError'), description: sanitizeErrorMessage(err), variant: 'destructive' });
      throw err;
    }
  }, [getState, saveDocument, currentDocumentId, setCurrentDocumentId, setWorkflowName, markClean, t]);

  /**
   * Direkt-uppdatera den för närvarande laddade cloud-kopian utan dialog.
   * Bevarar `category` och `is_official` från existerande raden.
   * Returnerar true om en update gjordes; false om ingen doc är laddad
   * (callern bör då öppna SaveDialog för "save as new").
   */
  const handleQuickUpdateToCloud = useCallback(async (): Promise<boolean> => {
    if (!currentDocumentId) return false;
    const title = (workflowName || '').trim();
    if (!title) {
      toast({
        title: t('saveError'),
        description: 'Workflow saknar namn — öppna Save-dialogen för att namnge.',
        variant: 'destructive',
      });
      return false;
    }
    let category = 'workflow';
    let isOfficial = false;
    try {
      const { data } = await supabase
        .from('workflows')
        .select('category, is_official')
        .eq('id', currentDocumentId)
        .maybeSingle();
      if (data) {
        if (typeof (data as { category?: unknown }).category === 'string') {
          category = (data as { category: string }).category;
        }
        if (typeof (data as { is_official?: unknown }).is_official === 'boolean') {
          isOfficial = (data as { is_official: boolean }).is_official;
        }
      }
    } catch {
      // ignore — fall back to defaults
    }
    await handleSaveToDb(title, category, false, isOfficial);
    return true;
  }, [currentDocumentId, workflowName, handleSaveToDb, t]);

  return { handleSave, handleSaveAs, handleSaveToDb, handleQuickUpdateToCloud };
}
